import { useState, useRef } from 'react';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useFormSubmit } from '../../../hooks/useFormSubmit';
import { useToast } from '../../../components/base/Toast';
import { PHONE_DISPLAY } from '../../../lib/businessInfo';

const ConsignmentSchema = z.object({
  name: z.string().min(2, 'Please enter your full name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(7, 'Please enter a valid phone number'),
  year: z.string().min(4, 'Please enter a valid year').max(4, 'Please enter a valid year'),
  make: z.string().min(1, 'Please enter the vehicle make'),
  model: z.string().min(1, 'Please enter the vehicle model'),
  mileage: z.string().min(1, 'Please enter the mileage'),
  condition: z.string().min(1, 'Please select a condition'),
  askingPrice: z.string().optional(),
  message: z.string().max(500, 'Message cannot exceed 500 characters').optional(),
});

export default function ConsignmentForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    year: '',
    make: '',
    model: '',
    mileage: '',
    condition: '',
    askingPrice: '',
    message: '',
  });

  const { handleSubmit, isSubmitting, isError, validationErrors } = useFormSubmit({
    url: 'https://readdy.ai/api/form/d6ja4pipaf6ievb71bpg',
    schema: ConsignmentSchema,
    formRef,
    errorRef,
    onSuccess: () => navigate('/thank-you?from=consignment'),
    onError: () => toast.error(
      `Failed to submit consignment inquiry. Please try again or call us at ${PHONE_DISPLAY}.`,
      { duration: 5000 }
    ),
    conversionLabel: 'CONSIGNMENT_LEAD_LABEL',
    leadEventName: 'Consignment Form Submission',
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fieldClass = (field: string) =>
    `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1C2B1E] focus:border-transparent transition-all text-sm ${
      validationErrors[field] ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'
    }`;

  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] p-8 shadow-sm">
      <h2 className="font-['Oswald'] text-3xl font-semibold text-[#1C2B1E] mb-2">Start Your Consignment</h2>
      <p className="text-[#374151] mb-8 text-sm">Tell us about your vehicle and we'll help you sell it for top dollar.</p>

      {/* Server / network error live region */}
      {isError && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 focus:outline-none"
        >
          <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
          <p className="text-red-700 text-sm">
            Something went wrong. Please try again or call us at {PHONE_DISPLAY}.
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6" id="consignment-form" data-readdy-form ref={formRef}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
              <i className="ri-user-line text-[#E8B84B]" aria-hidden="true"></i>Full Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required aria-required="true" autoComplete="name" aria-invalid={!!validationErrors.name} aria-describedby={validationErrors.name ? 'consignment-name-error' : undefined} className={fieldClass('name')} placeholder="John Doe" />
            {validationErrors.name && <p id="consignment-name-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
          </div>
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
              <i className="ri-mail-line text-[#E8B84B]" aria-hidden="true"></i>Email Address
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required aria-required="true" autoComplete="email" aria-invalid={!!validationErrors.email} aria-describedby={validationErrors.email ? 'consignment-email-error' : undefined} className={fieldClass('email')} placeholder="john@example.com" />
            {validationErrors.email && <p id="consignment-email-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
            <i className="ri-phone-line text-[#E8B84B]" aria-hidden="true"></i>Phone Number
          </label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required aria-required="true" autoComplete="tel" aria-invalid={!!validationErrors.phone} aria-describedby={validationErrors.phone ? 'consignment-phone-error' : undefined} className={fieldClass('phone')} placeholder="(555) 123-4567" />
          {validationErrors.phone && <p id="consignment-phone-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
        </div>

        <div className="border-t border-[#E8E2D6] pt-6">
          <h3 className="font-['Oswald'] text-xl font-semibold text-[#1C2B1E] mb-4">Vehicle Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="year" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-calendar-line text-[#E8B84B]" aria-hidden="true"></i>Year
              </label>
              <input type="text" id="year" name="year" value={formData.year} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.year} aria-describedby={validationErrors.year ? 'consignment-year-error' : undefined} className={fieldClass('year')} placeholder="2020" />
              {validationErrors.year && <p id="consignment-year-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.year}</p>}
            </div>
            <div>
              <label htmlFor="make" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-car-line text-[#E8B84B]" aria-hidden="true"></i>Make
              </label>
              <input type="text" id="make" name="make" value={formData.make} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.make} aria-describedby={validationErrors.make ? 'consignment-make-error' : undefined} className={fieldClass('make')} placeholder="Toyota" />
              {validationErrors.make && <p id="consignment-make-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.make}</p>}
            </div>
            <div>
              <label htmlFor="model" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-roadster-line text-[#E8B84B]" aria-hidden="true"></i>Model
              </label>
              <input type="text" id="model" name="model" value={formData.model} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.model} aria-describedby={validationErrors.model ? 'consignment-model-error' : undefined} className={fieldClass('model')} placeholder="Camry" />
              {validationErrors.model && <p id="consignment-model-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.model}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="mileage" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-dashboard-line text-[#E8B84B]" aria-hidden="true"></i>Mileage
              </label>
              <input type="text" id="mileage" name="mileage" value={formData.mileage} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.mileage} aria-describedby={validationErrors.mileage ? 'consignment-mileage-error' : undefined} className={fieldClass('mileage')} placeholder="50,000" />
              {validationErrors.mileage && <p id="consignment-mileage-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.mileage}</p>}
            </div>
            <div>
              <label htmlFor="condition" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-star-line text-[#E8B84B]" aria-hidden="true"></i>Condition
              </label>
              <select id="condition" name="condition" value={formData.condition} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.condition} aria-describedby={validationErrors.condition ? 'consignment-condition-error' : undefined} className={fieldClass('condition')}>
                <option value="">Select condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
              {validationErrors.condition && <p id="consignment-condition-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.condition}</p>}
            </div>
            <div>
              <label htmlFor="askingPrice" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
                <i className="ri-money-dollar-circle-line text-[#E8B84B]" aria-hidden="true"></i>Asking Price
              </label>
              <input type="text" id="askingPrice" name="askingPrice" value={formData.askingPrice} onChange={handleChange} className={fieldClass('askingPrice')} placeholder="$25,000" />
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-2">
            <i className="ri-message-3-line text-[#E8B84B]" aria-hidden="true"></i>Additional Details
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            aria-describedby={`consignment-message-count${validationErrors.message ? ' consignment-message-error' : ''}`}
            className={fieldClass('message')}
            placeholder="Tell us about special features, modifications, service history, or why you're selling..."
          />
          <div className="flex justify-between mt-1">
            {validationErrors.message ? <p id="consignment-message-error" role="alert" className="text-red-500 text-xs">{validationErrors.message}</p> : <span />}
            <p id="consignment-message-count" className="text-xs text-[#4A5568]" aria-live="polite">{formData.message.length}/500</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#1C2B1E] text-white py-4 rounded-lg font-semibold text-lg hover:bg-[#2d4430] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
        >
          {isSubmitting ? (
            <><i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>Submitting...</>
          ) : (
            <><i className="ri-send-plane-fill" aria-hidden="true"></i>Submit Consignment Request</>
          )}
        </button>
      </form>
    </div>
  );
}
